import React, { useState, useEffect } from 'react';
import {
  TrendingUp, TrendingDown, DollarSign, ShoppingCart, Package,
  Users, Clock, CheckCircle, Truck, Home, XCircle, AlertTriangle
} from 'lucide-react';
import { authGet } from '../services/api';

const AdminDashboardMain = () => {
  const [stats, setStats] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [lowStock, setLowStock] = useState([]);
  const [salesData, setSalesData] = useState([]);
  const [period, setPeriod] = useState('7days');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, [period]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [statsData, ordersData, productsData, stockData, salesDataRes] = await Promise.all([
        authGet('/api/admin/dashboard/stats'),
        authGet('/api/admin/orders/recent?limit=10'),
        authGet('/api/admin/analytics/top-products?limit=5'),
        authGet('/api/admin/inventory/low-stock?threshold=10'),
        authGet(`/api/admin/analytics/sales?period=${period}`)
      ]);

      setStats(statsData);
      setRecentOrders(ordersData.orders || []);
      setTopProducts(productsData.products || []);
      setLowStock(stockData.lowStockItems || []);
      setSalesData(salesDataRes.data || []);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: Clock,
      paid: CheckCircle,
      processing: Package,
      shipped: Truck,
      delivered: Home,
      cancelled: XCircle
    };
    return icons[status] || Clock;
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'text-orange-600 bg-orange-100',
      paid: 'text-green-600 bg-green-100',
      processing: 'text-blue-600 bg-blue-100',
      shipped: 'text-purple-600 bg-purple-100',
      delivered: 'text-teal-600 bg-teal-100',
      cancelled: 'text-red-600 bg-red-100'
    };
    return colors[status] || 'text-gray-600 bg-gray-100';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-orange-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600 font-bold uppercase tracking-wider text-sm">Loading Dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-2xl p-6 border-2 border-green-100 hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-green-600" strokeWidth={2.5} />
            </div>
            <div className="flex items-center gap-1 text-green-600 text-sm font-bold">
              <TrendingUp className="w-4 h-4" />
              <span>12%</span>
            </div>
          </div>
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Total Revenue</p>
          <p className="text-3xl font-black text-slate-900">₹{(stats?.totalRevenue || 0).toLocaleString('en-IN')}</p>
        </div>

        <div className="bg-white rounded-2xl p-6 border-2 border-blue-100 hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <ShoppingCart className="w-6 h-6 text-blue-600" strokeWidth={2.5} />
            </div>
            <div className="text-blue-600 text-xs font-bold">Today: {stats?.todayOrders || 0}</div>
          </div>
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Total Orders</p>
          <p className="text-3xl font-black text-slate-900">{stats?.totalOrders || 0}</p>
        </div>

        <div className="bg-white rounded-2xl p-6 border-2 border-orange-100 hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
              <Clock className="w-6 h-6 text-orange-600" strokeWidth={2.5} />
            </div>
            <div className="flex items-center gap-1 text-orange-600 text-sm font-bold">
              <AlertTriangle className="w-4 h-4" />
            </div>
          </div>
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Pending Orders</p>
          <p className="text-3xl font-black text-slate-900">{stats?.pendingOrders || 0}</p>
        </div>

        <div className="bg-white rounded-2xl p-6 border-2 border-purple-100 hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
              <Package className="w-6 h-6 text-purple-600" strokeWidth={2.5} />
            </div>
            <div className="flex items-center gap-1 text-purple-600 text-sm font-bold">
              <Users className="w-4 h-4" />
            </div>
          </div>
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Active Products</p>
          <p className="text-3xl font-black text-slate-900">{stats?.totalProducts || 0}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        {/* Recent Orders */}
        <div className="lg:col-span-2 bg-white rounded-2xl p-6 border border-slate-200">
          <h3 className="text-lg font-black text-slate-900 uppercase tracking-tight mb-6">Recent Orders</h3>
          <div className="space-y-3">
            {recentOrders.length > 0 ? (
              recentOrders.map((order) => {
                const StatusIcon = getStatusIcon(order.status);
                return (
                  <div key={order.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors">
                    <div className="flex items-center gap-4 flex-1">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${getStatusColor(order.status)}`}>
                        <StatusIcon className="w-5 h-5" strokeWidth={2.5} />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-bold text-slate-900">#{order.order_number}</p>
                        <p className="text-xs text-slate-500 font-medium">{order.customer_name}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-black text-slate-900">₹{order.total_amount?.toLocaleString('en-IN')}</p>
                      <p className={`text-xs font-bold uppercase tracking-wider ${getStatusColor(order.status).split(' ')[0]}`}>
                        {order.status}
                      </p>
                    </div>
                  </div>
                );
              })
            ) : (
              <p className="text-center text-slate-400 py-8">No recent orders</p>
            )}
          </div>
        </div>

        {/* Top Products */}
        <div className="bg-white rounded-2xl p-6 border border-slate-200">
          <h3 className="text-lg font-black text-slate-900 uppercase tracking-tight mb-6">Top Products</h3>
          <div className="space-y-3">
            {topProducts.length > 0 ? (
              topProducts.map((product, index) => (
                <div key={product.product_id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
                  <div className="w-8 h-8 bg-orange-600 text-white rounded-lg flex items-center justify-center font-black text-sm">
                    {index + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-slate-900 truncate">{product.product_name}</p>
                    <p className="text-xs text-slate-500">Sold: {product.total_quantity}</p>
                  </div>
                  <p className="text-sm font-black text-green-600">₹{product.total_revenue?.toLocaleString('en-IN')}</p>
                </div>
              ))
            ) : (
              <p className="text-center text-slate-400 py-8">No sales data</p>
            )}
          </div>
        </div>
      </div>

      {/* Low Stock Alert */}
      {lowStock.length > 0 && (
        <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <AlertTriangle className="w-6 h-6 text-red-600" strokeWidth={2.5} />
            <h3 className="text-lg font-black text-red-900 uppercase tracking-tight">Low Stock Alert</h3>
            <span className="ml-auto bg-red-600 text-white text-xs font-black px-3 py-1 rounded-full">
              {lowStock.length} Items
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {lowStock.map((item) => (
              <div key={item.id} className="bg-white rounded-xl p-4 border border-red-200">
                <p className="text-sm font-bold text-slate-900 mb-1">{item.product_name}</p>
                <p className="text-xs text-slate-500 mb-2">{item.variant_details}</p>
                <p className="text-lg font-black text-red-600">Stock: {item.available_quantity}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboardMain;
